// Hook for providing haptic feedback on mobile devices

import { useCallback } from 'react';

type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

export function useHapticFeedback() {
  const triggerHaptic = useCallback((type: HapticType = 'light') => {
    // Check if we're in a browser and if the Vibration API is available
    if (typeof navigator === 'undefined' || !('vibrate' in navigator)) {
      return;
    }

    // Different vibration patterns for different feedback types
    switch (type) {
      case 'light':
        navigator.vibrate(10);
        break;
      case 'medium':
        navigator.vibrate(20);
        break;
      case 'heavy':
        navigator.vibrate(30);
        break;
      case 'success':
        navigator.vibrate([10, 20, 10]); // Short pattern for success
        break;
      case 'warning':
        navigator.vibrate([20, 10, 20]); // Medium pattern for warning
        break;
      case 'error':
        navigator.vibrate([50, 30, 50]); // Longer pattern for error
        break;
      case 'selection':
        navigator.vibrate(15); // Quick feedback for selections
        break;
      default:
        navigator.vibrate(10);
    }
  }, []);

  // Helper functions for common use cases
  const hapticSelect = useCallback(() => triggerHaptic('selection'), [triggerHaptic]);
  const hapticSuccess = useCallback(() => triggerHaptic('success'), [triggerHaptic]);
  const hapticError = useCallback(() => triggerHaptic('error'), [triggerHaptic]);
  const hapticWarning = useCallback(() => triggerHaptic('warning'), [triggerHaptic]);
  const hapticNavigate = useCallback(() => triggerHaptic('light'), [triggerHaptic]);

  return {
    triggerHaptic,
    hapticSelect,
    hapticSuccess,
    hapticError,
    hapticWarning,
    hapticNavigate,
  };
}

// Custom hook for haptic feedback on button press
export function useHapticButton() {
  const { hapticSelect } = useHapticFeedback();

  const onPress = useCallback((callback?: () => void) => {
    return () => {
      hapticSelect();
      callback?.();
    };
  }, [hapticSelect]);

  return { onPress };
}
